import { supabase } from './supabaseClient';
import { DEFAULT_AAR_UPLOAD_ALLOWED_MIME_TYPES } from './aarDriveFolders';
import {
  AAR_REPORTING_EVENT_TYPES,
  EVENT_TYPES,
  deriveEventType,
  formatEventTypeLabel,
} from './eventTypes';
import { invokeStaffBlobFunction, invokeStaffFunction, invokeStaffMultipartFunction } from './staffEdgeFunctions';

export const AAR_MANAGE_ALL_CAPABILITY = 'aar_manage_all';
export const AAR_REVIEW_CAPABILITY = 'aar_review';
export const AAR_TEAM_CAPABILITIES = [AAR_MANAGE_ALL_CAPABILITY, AAR_REVIEW_CAPABILITY];
export const AAR_WORKFLOW_TYPE_FULL = 'full';
export const AAR_WORKFLOW_TYPE_PHOTO_ONLY = 'photo_only';
export const AAR_QUESTIONNAIRE_VERSION = 'v1';
export const AAR_HISTORICAL_EVENT_SOURCE = 'historical_google_form';
export const AAR_UPLOAD_ACCEPT_ATTRIBUTE = DEFAULT_AAR_UPLOAD_ALLOWED_MIME_TYPES.join(',');
export const AAR_UPLOAD_ALLOWED_FILE_TYPES_COPY = 'JPEG, PNG, WebP, HEIC, and HEIF';
export const AAR_UPLOAD_MAX_FILE_BYTES = 20 * 1024 * 1024;
export const HISTORICAL_AAR_DUPLICATE_RESOLUTION_OPTIONS = [
  {
    value: 'keeper',
    label: 'Keeper: promote this row, skip other true duplicates',
    shortLabel: 'Keeper',
    color: 'success',
  },
  {
    value: 'duplicate_skip',
    label: 'Duplicate, skip this row',
    shortLabel: 'Duplicate skip',
    color: 'warning',
  },
  {
    value: 'companion_photo_only',
    label: 'Companion photo-only submission',
    shortLabel: 'Companion photo-only',
    color: 'info',
  },
  {
    value: 'not_duplicate',
    label: 'Not a duplicate',
    shortLabel: 'Not a duplicate',
    color: 'default',
  },
  {
    value: 'unclear',
    label: 'Unclear, needs more review',
    shortLabel: 'Unclear',
    color: 'default',
  },
];

const AAR_MANAGED_PAYLOAD_KEYS = [
  'workflow',
  'event',
  'submitter',
  'staff',
  'attendance',
  'results',
  'recommendation',
  'follow_up',
];

export const AAR_FORMAT_OPTIONS = [
  { value: 'in_person', label: 'In-Person' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'hybrid', label: 'Hybrid/Both' },
];

const AAR_US_LOCATION_OPTIONS = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

export const AAR_LOCATION_OPTIONS = [
  ...AAR_US_LOCATION_OPTIONS,
  'Puerto Rico',
  'U.S. Virgin Islands',
  'Washington, DC',
  'Online',
  'Multiple states',
  'Other',
];

export const AAR_RHP_INVOLVEMENT_OPTIONS = [
  'Presentation',
  'Resource Table',
  'Speaking Engagement',
  'Area Blitz',
  'Networking',
  'Fundraising/Philanthropy',
  'Event Coordination',
  'Volunteer',
  'Media Interview',
  'Social Media Engagement',
  'Training/Professional Development',
  'Clinic Tour',
  'Other',
];

export const AAR_PAST_ATTENDANCE_OPTIONS = ['Yes', 'No', 'Unsure'];
export const AAR_WORTHY_ATTENDANCE_OPTIONS = ['Yes', 'No', 'Maybe'];
export const AAR_ATTENDANCE_AGAIN_OPTIONS = ['Yes', 'No', 'Maybe', 'Not Possible'];
export const AAR_NUMERIC_FIELD_CONFIG = {
  veterans: { label: '# of Veterans', kind: 'integer' },
  serviceMembers: { label: '# of Service Members', kind: 'integer' },
  militaryFamily: { label: '# of Military Family', kind: 'integer' },
  clinicians: { label: '# of Clinicians', kind: 'integer' },
  vsoStaff: { label: '# of VSO Staff', kind: 'integer' },
  corporateInstitutionalStaff: { label: '# of Corporate or Institutional Staff', kind: 'integer' },
  attendanceOther: { label: '# of Other', kind: 'integer' },
  getCareFormSubmissions: { label: 'Number of Get Care Form submissions', kind: 'integer' },
  contactUsFormSubmissions: { label: 'Number of Contact Us Form submissions', kind: 'integer' },
  recommendedRepresentatives: { label: 'Recommended number of RHP representatives', kind: 'integer' },
  fees: { label: 'Fees / cost, if any', kind: 'decimal' },
};

export const AAR_EVENT_SELECT_FIELDS = [
  'id',
  'name',
  'date_utc',
  'timezone',
  'event_category',
  'event_region',
  'source',
  'event_type',
  'source_label',
  'external_managed',
].join(', ');

export const AAR_LIST_SELECT_FIELDS = [
  'id',
  'event_id',
  'owner_id',
  'created_by',
  'workflow_type',
  'status',
  'event_name_snapshot',
  'event_date_utc_snapshot',
  'photo_count',
  'submitted_at',
  'created_at',
  'updated_at',
].join(', ');

export const AAR_DETAIL_SELECT_FIELDS = [
  'id',
  'event_id',
  'owner_id',
  'created_by',
  'updated_by',
  'workflow_type',
  'status',
  'questionnaire_version',
  'event_name_snapshot',
  'event_date_utc_snapshot',
  'google_drive_folder_id',
  'google_drive_folder_url',
  'photo_count',
  'attendance_estimate',
  'follow_up_required',
  'submitted_at',
  'reviewed_at',
  'reviewed_by',
  'aar_payload_json',
  'created_at',
  'updated_at',
].join(', ');

export const AAR_REPORTING_SELECT_FIELDS = [
  'aar_id',
  'event_id',
  'event_name',
  'event_date_utc',
  'event_source',
  'source_label',
  'workflow_type',
  'status',
  'owner_id',
  'owner_display',
  'created_by',
  'created_by_display',
  'submitted_at',
  'reviewed_at',
  'reviewed_by',
  'attendance_estimate',
  'stored_photo_count',
  'photo_count_snapshot',
  'follow_up_required',
  'google_drive_folder_id',
  'google_drive_folder_url',
  'created_at',
  'updated_at',
  'created_month',
  'is_historical_import',
  'is_manual_aar',
  'is_photo_only',
  'import_batch_id',
  'source_import_row_id',
  'historical_import_status',
  'event_type',
].join(', ');

export const AAR_IMPORT_REVIEW_SELECT_FIELDS = [
  'batch_id',
  'id',
  'source_row_number',
  'import_status',
  'event_title_raw',
  'event_date_raw',
  'event_date_normalized',
  'submitter_name_raw',
  'submitter_name_normalized',
  'workflow_guess',
  'workflow_confidence',
  'photo_link_count',
  'duplicate_group_key',
  'issue_flags',
  'blocking_issue_flags',
  'informational_issue_flags',
  'review_reason_categories',
  'recommended_action',
  'reviewer_decision',
  'corrected_event_title',
  'corrected_event_date',
  'corrected_submitter_name',
  'corrected_workflow_type',
  'corrected_state',
  'duplicate_resolution',
  'reviewer_notes',
  'error_message',
  'promoted_aar_id',
  'promoted_event_id',
  'created_at',
  'updated_at',
].join(', ');

export const AAR_PHOTO_SELECT_FIELDS = [
  'id',
  'aar_id',
  'event_id',
  'provider',
  'drive_file_id',
  'drive_folder_id',
  'storage_bucket',
  'storage_path',
  'filename',
  'mime_type',
  'file_size_bytes',
  'width',
  'height',
  'captured_at',
  'web_view_link',
  'thumbnail_link',
  'md5_checksum',
  'sort_order',
  'created_at',
  'updated_at',
].join(', ');

const HISTORICAL_AAR_DUPLICATE_RESOLUTION_MAP = HISTORICAL_AAR_DUPLICATE_RESOLUTION_OPTIONS.reduce(
  (accumulator, option) => ({
    ...accumulator,
    [option.value]: option,
  }),
  {}
);
const HISTORICAL_AAR_DUPLICATE_RESOLUTION_ALIASES = {
  'keeper for duplicate group. most complete/accurate row.': 'keeper',
};

function normalizeText(value) {
  return String(value ?? '').trim().toLowerCase();
}

function trimText(value) {
  return String(value ?? '').trim();
}

function normalizeStringArray(values = []) {
  return (Array.isArray(values) ? values : [])
    .map((value) => trimText(value))
    .filter(Boolean);
}

export function cleanAarPayloadEntries(entries = []) {
  return Object.fromEntries(entries.filter(([, value]) => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === 'object') return Object.keys(value).length > 0;
    return value !== null && value !== undefined && trimText(value) !== '';
  }));
}

function readAarNumericValue(value, kind = 'integer') {
  const normalizedValue = trimText(value);
  if (!normalizedValue) {
    return {
      value: null,
      isEmpty: true,
      isValid: true,
    };
  }

  const sanitizedValue = normalizedValue.replace(/[$,\s]/g, '');
  const numericPattern = kind === 'decimal'
    ? /^-?\d+(\.\d+)?$/
    : /^-?\d+$/;

  if (!numericPattern.test(sanitizedValue)) {
    return {
      value: null,
      isEmpty: false,
      isValid: false,
    };
  }

  const parsedValue = kind === 'decimal'
    ? Number.parseFloat(sanitizedValue)
    : Number.parseInt(sanitizedValue, 10);

  if (!Number.isFinite(parsedValue)) {
    return {
      value: null,
      isEmpty: false,
      isValid: false,
    };
  }

  return {
    value: parsedValue,
    isEmpty: false,
    isValid: true,
  };
}

function buildAarNumericErrorMessage(fieldName, value) {
  const config = AAR_NUMERIC_FIELD_CONFIG[fieldName];
  if (!config) return '';

  const parsedValue = readAarNumericValue(value, config.kind);
  if (parsedValue.isEmpty) return '';
  if (!parsedValue.isValid) {
    return config.kind === 'decimal'
      ? `${config.label} must be a valid dollar amount.`
      : `${config.label} must be a valid whole number.`;
  }
  if (parsedValue.value < 0) return `${config.label} cannot be negative.`;
  return '';
}

export function getAarNumericFieldErrors(formValues = {}) {
  return Object.entries(AAR_NUMERIC_FIELD_CONFIG).reduce((errors, [fieldName]) => {
    const message = buildAarNumericErrorMessage(fieldName, formValues?.[fieldName]);
    if (!message) return errors;
    return {
      ...errors,
      [fieldName]: message,
    };
  }, {});
}

export function getNormalizedAarNumericValues(formValues = {}, { rejectInvalid = false } = {}) {
  const errors = getAarNumericFieldErrors(formValues);
  if (rejectInvalid && Object.keys(errors).length > 0) {
    throw new Error(Object.values(errors)[0]);
  }

  return Object.entries(AAR_NUMERIC_FIELD_CONFIG).reduce((values, [fieldName, config]) => {
    const hasError = Boolean(errors[fieldName]);
    const parsedValue = hasError
      ? null
      : readAarNumericValue(formValues?.[fieldName], config.kind).value;

    return {
      ...values,
      [fieldName]: parsedValue,
    };
  }, {});
}

export function normalizeCapability(value) {
  return normalizeText(value);
}

export function hasAarCapability(capabilities = [], capability) {
  const normalizedCapability = normalizeCapability(capability);
  if (!normalizedCapability) return false;

  return (Array.isArray(capabilities) ? capabilities : [])
    .map(normalizeCapability)
    .filter(Boolean)
    .includes(normalizedCapability);
}

export function hasAarManageAllAccess(capabilities = []) {
  return hasAarCapability(capabilities, AAR_MANAGE_ALL_CAPABILITY);
}

export function hasAarReviewAccess(capabilities = []) {
  return hasAarManageAllAccess(capabilities) || hasAarCapability(capabilities, AAR_REVIEW_CAPABILITY);
}

export function hasAarTeamAccess(capabilities = []) {
  const normalizedCapabilities = new Set(
    (Array.isArray(capabilities) ? capabilities : []).map(normalizeCapability).filter(Boolean)
  );

  return AAR_TEAM_CAPABILITIES.some((capability) => normalizedCapabilities.has(capability));
}

export function canEditAar({ aar = null, userId, capabilities = [] } = {}) {
  if (!aar || !userId) return false;
  if (hasAarManageAllAccess(capabilities)) return true;

  const isOwnAar = aar.owner_id === userId || aar.created_by === userId;
  return isOwnAar && normalizeText(aar.status) !== 'reviewed';
}

export function canUploadAar({ aar = null, userId, capabilities = [] } = {}) {
  if (!aar || !userId) return false;
  if (hasAarManageAllAccess(capabilities)) return true;

  const isOwnAar = aar.owner_id === userId || aar.created_by === userId;
  return isOwnAar && normalizeText(aar.status) !== 'reviewed';
}

export function canMarkAarReviewed({ aar = null, capabilities = [] } = {}) {
  if (!aar) return false;
  if (normalizeText(aar.status) !== 'submitted') return false;
  return hasAarReviewAccess(capabilities);
}

export function formatAarWorkflowTypeLabel(value) {
  if (normalizeText(value) === 'photo_only') return 'Photo-only';
  if (normalizeText(value) === 'full') return 'Full AAR';
  return 'Unknown workflow';
}

export function formatAarStatusLabel(value) {
  const normalizedValue = normalizeText(value);

  if (normalizedValue === 'draft') return 'Draft';
  if (normalizedValue === 'submitted') return 'Submitted';
  if (normalizedValue === 'reviewed') return 'Reviewed';
  if (normalizedValue === 'archived') return 'Archived';
  return 'Unknown status';
}

export function formatAarImportStatusLabel(value) {
  const normalizedValue = normalizeText(value);

  if (normalizedValue === 'needs_review') return 'Needs Review';
  if (normalizedValue === 'approved') return 'Approved';
  if (normalizedValue === 'skipped') return 'Skipped';
  if (normalizedValue === 'promoted') return 'Promoted';
  if (normalizedValue === 'error') return 'Error';
  if (normalizedValue === 'staged') return 'Staged';
  return 'Unknown status';
}

export function formatAarDateTime(value, options = {}) {
  const {
    fallback = '—',
    dateStyle = 'medium',
    timeStyle,
  } = options;

  if (!value) return fallback;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;

  return new Intl.DateTimeFormat(undefined, {
    dateStyle,
    ...(timeStyle ? { timeStyle } : {}),
  }).format(date);
}

export function getAarEventName(row = {}) {
  return row?.event_name_snapshot || row?.event_name || row?.name || row?.event_id || 'Untitled event';
}

export function getAarLinkedEvent(row = {}) {
  if (row?.linked_event && typeof row.linked_event === 'object') {
    return row.linked_event;
  }

  return null;
}

export function getAarEventSource(row = {}) {
  return normalizeText(getAarLinkedEvent(row)?.source || row?.event_source || row?.source);
}

export function getAarEventSourceLabel(row = {}) {
  return trimText(getAarLinkedEvent(row)?.source_label || row?.source_label);
}

export function getAarEventType(row = {}) {
  return deriveEventType({
    event_type: getAarLinkedEvent(row)?.event_type || row?.event_type,
    source: getAarLinkedEvent(row)?.source || row?.event_source || row?.source,
    external_managed: getAarLinkedEvent(row)?.external_managed ?? row?.external_managed,
  });
}

export function getAarEventTypeLabel(row = {}) {
  return formatEventTypeLabel(getAarEventType(row), { short: true });
}

export function isHistoricalGoogleFormSource(value) {
  return normalizeText(value) === AAR_HISTORICAL_EVENT_SOURCE;
}

export function isHistoricalImportedAar(row = {}) {
  if (row?.is_historical_import === true) return true;
  if (isHistoricalGoogleFormSource(getAarEventSource(row))) return true;
  if (normalizeText(getAarEventSourceLabel(row)) === 'historical google form aar') return true;
  return Boolean(getAarImportMetadata(row));
}

export function isManualAar(row = {}) {
  if (row?.is_manual_aar === true) return true;
  if (getAarEventType(row) === EVENT_TYPES.aarShell && !isHistoricalImportedAar(row)) return true;
  return normalizeText(getAarEventSource(row)) === 'manual_aar';
}

export function isPhotoOnlyAar(row = {}) {
  if (row?.is_photo_only === true) return true;
  return normalizeText(row?.workflow_type) === AAR_WORKFLOW_TYPE_PHOTO_ONLY;
}

export function getAarStoredPhotoCount(row = {}) {
  const storedPhotoCount = Number(row?.stored_photo_count);
  if (Number.isFinite(storedPhotoCount) && storedPhotoCount >= 0) return storedPhotoCount;

  const snapshotPhotoCount = Number(row?.photo_count_snapshot ?? row?.photo_count);
  if (Number.isFinite(snapshotPhotoCount) && snapshotPhotoCount >= 0) return snapshotPhotoCount;

  return 0;
}

export function getHistoricalAarReviewEffectiveTitle(row = {}) {
  return trimText(row?.corrected_event_title) || trimText(row?.event_title_raw) || 'Untitled event';
}

export function normalizeHistoricalAarDuplicateResolution(value) {
  const normalizedValue = normalizeText(value);
  const canonicalValue = HISTORICAL_AAR_DUPLICATE_RESOLUTION_ALIASES[normalizedValue] || normalizedValue;
  return HISTORICAL_AAR_DUPLICATE_RESOLUTION_MAP[canonicalValue] ? canonicalValue : '';
}

export function formatHistoricalAarDuplicateResolutionLabel(value, { short = false } = {}) {
  const normalizedValue = normalizeHistoricalAarDuplicateResolution(value);
  if (normalizedValue) {
    const option = HISTORICAL_AAR_DUPLICATE_RESOLUTION_MAP[normalizedValue];
    return short ? option.shortLabel : option.label;
  }

  return trimText(value) || '—';
}

export function getHistoricalAarDuplicateResolutionBadge(value) {
  const normalizedValue = normalizeHistoricalAarDuplicateResolution(value);
  if (!normalizedValue) return null;

  const option = HISTORICAL_AAR_DUPLICATE_RESOLUTION_MAP[normalizedValue];
  return {
    label: option.shortLabel,
    color: option.color,
    variant: 'outlined',
  };
}

export function canApproveHistoricalAarDuplicateCandidate(value) {
  return ['keeper', 'companion_photo_only', 'not_duplicate'].includes(
    normalizeHistoricalAarDuplicateResolution(value)
  );
}

export function canSkipHistoricalAarDuplicateCandidate(value) {
  return normalizeHistoricalAarDuplicateResolution(value) === 'duplicate_skip';
}

export function getHistoricalAarReviewEffectiveSubmitter(row = {}) {
  return trimText(row?.corrected_submitter_name)
    || trimText(row?.submitter_name_normalized)
    || trimText(row?.submitter_name_raw)
    || '—';
}

export function getHistoricalAarReviewEffectiveWorkflow(row = {}) {
  const workflowValue = trimText(row?.corrected_workflow_type) || trimText(row?.workflow_guess);
  if (normalizeText(workflowValue) === AAR_WORKFLOW_TYPE_PHOTO_ONLY) return 'Photo-only';
  if (normalizeText(workflowValue) === AAR_WORKFLOW_TYPE_FULL) return 'Full AAR';
  if (workflowValue) return workflowValue;
  return '—';
}

export function getHistoricalAarReviewEffectiveDate(row = {}) {
  return row?.corrected_event_date || row?.event_date_normalized || trimText(row?.event_date_raw) || '';
}

export function normalizeHistoricalAarReviewDraft(draft = {}) {
  return {
    corrected_event_title: nullifEmpty(draft?.corrected_event_title),
    corrected_event_date: nullifEmpty(draft?.corrected_event_date),
    corrected_submitter_name: nullifEmpty(draft?.corrected_submitter_name),
    corrected_workflow_type: nullifEmpty(draft?.corrected_workflow_type),
    corrected_state: nullifEmpty(draft?.corrected_state),
    duplicate_resolution: nullifEmpty(draft?.duplicate_resolution),
    reviewer_notes: nullifEmpty(draft?.reviewer_notes),
  };
}

export function applyHistoricalAarReviewDraft(row = {}, draft = {}) {
  if (!row) return null;

  const normalizedDraft = normalizeHistoricalAarReviewDraft(draft);

  return {
    ...row,
    corrected_event_title: normalizedDraft.corrected_event_title,
    corrected_event_date: normalizedDraft.corrected_event_date,
    corrected_submitter_name: normalizedDraft.corrected_submitter_name,
    corrected_workflow_type: normalizedDraft.corrected_workflow_type,
    corrected_state: normalizedDraft.corrected_state,
    duplicate_resolution: normalizedDraft.duplicate_resolution,
    reviewer_notes: normalizedDraft.reviewer_notes,
  };
}

export function hasHistoricalAarReviewDuplicateCandidate(row = {}) {
  const candidateValues = [
    ...(Array.isArray(row?.issue_flags) ? row.issue_flags : []),
    ...(Array.isArray(row?.blocking_issue_flags) ? row.blocking_issue_flags : []),
    ...(Array.isArray(row?.informational_issue_flags) ? row.informational_issue_flags : []),
    ...(Array.isArray(row?.review_reason_categories) ? row.review_reason_categories : []),
  ];

  return candidateValues.some((value) => normalizeText(value) === 'duplicate_candidate');
}

export function formatHistoricalAarReviewEffectiveDate(row = {}) {
  if (row?.corrected_event_date) {
    return formatAarDateTime(row.corrected_event_date, { fallback: '—' });
  }

  if (row?.event_date_normalized) {
    return formatAarDateTime(row.event_date_normalized, { fallback: '—' });
  }

  return trimText(row?.event_date_raw) || '—';
}

export function getAarBadgeConfig(row = {}) {
  const badges = [];

  if (isHistoricalImportedAar(row)) {
    badges.push({
      key: 'historical-import',
      label: 'Historical Import',
      color: 'warning',
      variant: 'filled',
    });
  }

  if (isManualAar(row)) {
    badges.push({
      key: 'manual-aar',
      label: 'Manual AAR',
      color: 'default',
      variant: 'outlined',
    });
  }

  if (isPhotoOnlyAar(row)) {
    badges.push({
      key: 'photo-only',
      label: 'Photo-only',
      color: 'secondary',
      variant: 'outlined',
    });
  }

  if (normalizeText(row?.status) === 'reviewed') {
    badges.push({
      key: 'reviewed',
      label: 'Reviewed',
      color: 'success',
      variant: 'filled',
    });
  }

  if (normalizeText(row?.status) === 'submitted') {
    badges.push({
      key: 'submitted',
      label: 'Submitted',
      color: 'info',
      variant: 'filled',
    });
  }

  return badges;
}

export function getAarImportMetadata(row = {}) {
  const importMetadata = row?.aar_payload_json?.import;
  if (!importMetadata || typeof importMetadata !== 'object' || Array.isArray(importMetadata)) {
    return null;
  }

  return importMetadata;
}

export function getProfileDisplayName(profile = null) {
  if (!profile) return '';
  return trimText(profile?.full_name) || trimText(profile?.email) || trimText(profile?.id) || '';
}

export function getDefaultAarSubmitterName({ profile = null, user = null } = {}) {
  return trimText(profile?.full_name)
    || trimText(user?.user_metadata?.full_name)
    || trimText(profile?.email)
    || trimText(user?.email)
    || '';
}

export function getAarDefaultWorkflowType(value) {
  if (normalizeText(value) === AAR_WORKFLOW_TYPE_PHOTO_ONLY) return AAR_WORKFLOW_TYPE_PHOTO_ONLY;
  return AAR_WORKFLOW_TYPE_FULL;
}

export function buildAarCreateRedirectPath({ aarId, workflowType } = {}) {
  if (!trimText(aarId)) return '/me/aars';
  if (getAarDefaultWorkflowType(workflowType) === AAR_WORKFLOW_TYPE_PHOTO_ONLY) {
    return `/me/aars/${aarId}/photos`;
  }

  return `/me/aars/${aarId}`;
}

export function buildAarPhotoUploadQueueItems(files = []) {
  return Array.from(files || []).map((file, index) => ({
    id: `${file?.name || 'photo'}-${file?.size || 0}-${file?.lastModified || 0}-${index}`,
    name: trimText(file?.name) || `Photo ${index + 1}`,
    file,
    index,
    status: 'queued',
    message: 'Queued for upload.',
  }));
}

export function getBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_error) {
    return 'UTC';
  }
}

export function isAarOtherLocation(value) {
  return normalizeText(value) === 'other';
}

export function parseNullableInteger(value) {
  const normalizedValue = trimText(value);
  if (!normalizedValue) return null;

  const parsed = Number.parseInt(normalizedValue.replace(/[^0-9-]/g, ''), 10);
  if (Number.isNaN(parsed)) return null;
  return parsed;
}

export function normalizeAarLinks(value) {
  return trimText(value)
    .split(/[\n,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function calculateAarAttendanceEstimate(attendance = {}) {
  const values = [
    attendance.veterans,
    attendance.service_members,
    attendance.military_family,
    attendance.clinicians,
    attendance.vso_staff,
    attendance.corporate_or_institutional_staff,
    attendance.other,
  ]
    .map(parseNullableInteger)
    .filter((value) => Number.isInteger(value) && value >= 0);

  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0);
}

export function convertAarDateInputToUtcIso(dateInput) {
  const normalizedValue = trimText(dateInput);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) return null;

  const [year, month, day] = normalizedValue.split('-').map((value) => Number.parseInt(value, 10));
  const localDate = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (Number.isNaN(localDate.getTime())) return null;

  return localDate.toISOString();
}

export function buildAarPayload(formValues = {}, options = {}) {
  const {
    rejectInvalidNumbers = false,
    normalizedNumericValues = getNormalizedAarNumericValues(formValues, { rejectInvalid: rejectInvalidNumbers }),
  } = options;
  const workflowType = getAarDefaultWorkflowType(formValues.workflowType);
  const event = cleanAarPayloadEntries([
    ['title', trimText(formValues.eventTitle)],
    ['date_local', trimText(formValues.dateOfEvent)],
    ['format', workflowType === AAR_WORKFLOW_TYPE_FULL ? trimText(formValues.format) : ''],
    ['location', workflowType === AAR_WORKFLOW_TYPE_FULL ? trimText(formValues.locationCity) : ''],
    ['state', workflowType === AAR_WORKFLOW_TYPE_FULL ? trimText(formValues.stateLocation) : ''],
    ['state_other', workflowType === AAR_WORKFLOW_TYPE_FULL && isAarOtherLocation(formValues.stateLocation)
      ? trimText(formValues.stateOther)
      : ''],
  ]);

  const payload = {
    workflow: {
      type: workflowType,
      is_this_for_an_aar: workflowType === AAR_WORKFLOW_TYPE_FULL ? 'Yes' : 'No',
    },
    event,
    submitter: {
      name_raw: trimText(formValues.submitterName),
    },
  };

  if (workflowType === AAR_WORKFLOW_TYPE_PHOTO_ONLY) {
    payload.media = {
      upload_summary: 'Photos upload to private AAR photo storage after the AAR is saved.',
    };
    return payload;
  }

  const attendance = cleanAarPayloadEntries([
    ['veterans', normalizedNumericValues.veterans],
    ['service_members', normalizedNumericValues.serviceMembers],
    ['military_family', normalizedNumericValues.militaryFamily],
    ['clinicians', normalizedNumericValues.clinicians],
    ['vso_staff', normalizedNumericValues.vsoStaff],
    ['corporate_or_institutional_staff', normalizedNumericValues.corporateInstitutionalStaff],
    ['other', normalizedNumericValues.attendanceOther],
  ]);

  payload.staff = cleanAarPayloadEntries([
    ['poc', trimText(formValues.poc)],
    ['rhp_staff', trimText(formValues.rhpStaffInvolved)],
    ['intent', trimText(formValues.intent)],
    ['fees', normalizedNumericValues.fees],
  ]);
  payload.attendance = attendance;
  payload.results = cleanAarPayloadEntries([
    ['organizations_involved', trimText(formValues.organizationsInvolved)],
    ['rhp_involvement', Array.isArray(formValues.rhpInvolvement)
      ? formValues.rhpInvolvement.map(trimText).filter(Boolean)
      : []],
    ['get_care_form_submissions', normalizedNumericValues.getCareFormSubmissions],
    ['contact_us_form_submissions', normalizedNumericValues.contactUsFormSubmissions],
    ['immediate_results_other', trimText(formValues.otherImmediateResults)],
    ['long_term_results', trimText(formValues.longTermResults)],
  ]);
  payload.recommendation = cleanAarPayloadEntries([
    ['past_attendance', trimText(formValues.pastAttendance)],
    ['worthy_attendance', trimText(formValues.worthyAttendance)],
    ['attendance_again', trimText(formValues.attendanceAgain)],
    ['recommended_rhp_representatives', normalizedNumericValues.recommendedRepresentatives],
  ]);
  payload.follow_up = cleanAarPayloadEntries([
    ['issues', trimText(formValues.issues)],
    ['links', normalizeAarLinks(formValues.links)],
  ]);

  return payload;
}

export function mergeAarPayloadForSave(existingPayload = {}, nextPayload = {}) {
  const mergedPayload = {
    ...(existingPayload && typeof existingPayload === 'object' ? existingPayload : {}),
  };

  AAR_MANAGED_PAYLOAD_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(nextPayload, key)) {
      mergedPayload[key] = nextPayload[key];
      return;
    }

    delete mergedPayload[key];
  });

  if (Object.prototype.hasOwnProperty.call(nextPayload, 'media')) {
    mergedPayload.media = nextPayload.media;
  }

  return cleanAarPayloadEntries(Object.entries(mergedPayload));
}

export function buildManualAarEventRecord(formValues = {}, { profileId, timeZone } = {}) {
  const eventDateUtc = convertAarDateInputToUtcIso(formValues.dateOfEvent);
  const stateLabel = isAarOtherLocation(formValues.stateLocation)
    ? trimText(formValues.stateOther)
    : trimText(formValues.stateLocation);
  const generatedEventId = typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : `manual-aar-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  return cleanAarPayloadEntries([
    ['id', generatedEventId],
    ['name', trimText(formValues.eventTitle)],
    ['date_utc', eventDateUtc],
    ['timezone', trimText(timeZone) || 'UTC'],
    ['total_capacity', 0],
    ['event_region', stateLabel],
    ['owner_id', profileId],
    ['created_by', profileId],
    ['source', 'manual_aar'],
    ['event_type', EVENT_TYPES.aarShell],
    ['external_managed', false],
    ['source_label', 'Manual AAR'],
    ['is_public', false],
  ]);
}

export function buildManualAarEventUpdate(formValues = {}) {
  const stateLabel = isAarOtherLocation(formValues.stateLocation)
    ? trimText(formValues.stateOther)
    : trimText(formValues.stateLocation);

  return cleanAarPayloadEntries([
    ['name', trimText(formValues.eventTitle)],
    ['date_utc', convertAarDateInputToUtcIso(formValues.dateOfEvent)],
    ['event_region', stateLabel],
  ]);
}

export function shouldAttemptManualAarEventUpdate(eventRow = null) {
  if (!eventRow?.id) return false;
  return (
    getAarEventType(eventRow) === EVENT_TYPES.aarShell
    && normalizeText(eventRow.source) === 'manual_aar'
    && eventRow.external_managed === false
  );
}

export async function createAarSubmission({
  formValues,
  userId,
  client = supabase,
  timeZone = getBrowserTimeZone(),
} = {}) {
  if (!userId) throw new Error('A signed-in user is required to submit an AAR.');
  const numericValues = getNormalizedAarNumericValues(formValues, { rejectInvalid: true });

  const eventRecord = buildManualAarEventRecord(formValues, { profileId: userId, timeZone });
  const { data: eventRow, error: eventError } = await client
    .from('events')
    .insert(eventRecord)
    .select('id')
    .single();

  if (eventError) throw eventError;

  const workflowType = getAarDefaultWorkflowType(formValues?.workflowType);
  const submittedAt = new Date().toISOString();
  const eventDateUtc = convertAarDateInputToUtcIso(formValues?.dateOfEvent);
  const attendanceEstimate = workflowType === AAR_WORKFLOW_TYPE_FULL
    ? calculateAarAttendanceEstimate({
      veterans: numericValues.veterans,
      service_members: numericValues.serviceMembers,
      military_family: numericValues.militaryFamily,
      clinicians: numericValues.clinicians,
      vso_staff: numericValues.vsoStaff,
      corporate_or_institutional_staff: numericValues.corporateInstitutionalStaff,
      other: numericValues.attendanceOther,
    })
    : null;

  const aarRecord = {
    event_id: eventRow?.id ?? null,
    owner_id: userId,
    created_by: userId,
    updated_by: userId,
    workflow_type: workflowType,
    status: 'submitted',
    questionnaire_version: AAR_QUESTIONNAIRE_VERSION,
    event_name_snapshot: trimText(formValues?.eventTitle),
    event_date_utc_snapshot: eventDateUtc,
    attendance_estimate: attendanceEstimate,
    submitted_at: submittedAt,
    aar_payload_json: buildAarPayload(formValues, {
      rejectInvalidNumbers: true,
      normalizedNumericValues: numericValues,
    }),
  };

  const { data: aarRow, error: aarError } = await client
    .from('after_action_reports')
    .insert(aarRecord)
    .select('id, event_id')
    .single();

  if (aarError) throw aarError;

  return {
    aarId: aarRow?.id ?? null,
    eventId: aarRow?.event_id ?? eventRow?.id ?? null,
  };
}

export async function updateAarSubmission({
  aarId,
  formValues,
  currentUserId,
  existingAar = null,
  existingEvent = null,
  client = supabase,
} = {}) {
  if (!aarId) throw new Error('An AAR id is required to save edits.');
  if (!currentUserId) throw new Error('A signed-in user is required to edit an AAR.');

  const aarRow = existingAar || await fetchAarById({ aarId, client });
  if (!aarRow) throw new Error('This AAR was not found or is no longer visible.');

  const workflowType = getAarDefaultWorkflowType(aarRow.workflow_type || formValues?.workflowType);
  const nextFormValues = {
    ...formValues,
    workflowType,
  };
  const numericValues = getNormalizedAarNumericValues(nextFormValues, { rejectInvalid: true });
  const nextPayload = buildAarPayload(nextFormValues, {
    rejectInvalidNumbers: true,
    normalizedNumericValues: numericValues,
  });
  const eventDateUtc = convertAarDateInputToUtcIso(nextFormValues.dateOfEvent);
  const attendanceEstimate = workflowType === AAR_WORKFLOW_TYPE_FULL
    ? calculateAarAttendanceEstimate({
      veterans: numericValues.veterans,
      service_members: numericValues.serviceMembers,
      military_family: numericValues.militaryFamily,
      clinicians: numericValues.clinicians,
      vso_staff: numericValues.vsoStaff,
      corporate_or_institutional_staff: numericValues.corporateInstitutionalStaff,
      other: numericValues.attendanceOther,
    })
    : null;

  const aarUpdate = {
    updated_by: currentUserId,
    workflow_type: workflowType,
    event_name_snapshot: trimText(nextFormValues.eventTitle),
    event_date_utc_snapshot: eventDateUtc,
    attendance_estimate: attendanceEstimate,
    aar_payload_json: mergeAarPayloadForSave(aarRow.aar_payload_json, nextPayload),
  };

  const { data: updatedAarRow, error: aarError } = await client
    .from('after_action_reports')
    .update(aarUpdate)
    .eq('id', aarRow.id)
    .select('id, event_id')
    .single();

  if (aarError) throw aarError;

  const linkedEvent = existingEvent || await fetchAarEvent({ eventId: aarRow.event_id, client });
  if (shouldAttemptManualAarEventUpdate(linkedEvent)) {
    const eventUpdate = buildManualAarEventUpdate(nextFormValues);
    const { error: eventError } = await client
      .from('events')
      .update(eventUpdate)
      .eq('id', linkedEvent.id);

    if (eventError) throw eventError;
  }

  return {
    aarId: updatedAarRow?.id ?? aarRow.id,
    eventId: updatedAarRow?.event_id ?? aarRow.event_id ?? null,
  };
}

export async function markAarReviewed({
  aarId,
  currentUserId,
  client = supabase,
} = {}) {
  if (!aarId) throw new Error('An AAR id is required to mark an AAR reviewed.');
  if (!currentUserId) throw new Error('A signed-in user is required to mark an AAR reviewed.');

  const reviewUpdate = {
    status: 'reviewed',
    reviewed_at: new Date().toISOString(),
    reviewed_by: currentUserId,
    updated_by: currentUserId,
  };

  const { data, error } = await client
    .from('after_action_reports')
    .update(reviewUpdate)
    .eq('id', aarId)
    .eq('status', 'submitted')
    .select('id, status, reviewed_at, reviewed_by, updated_by')
    .single();

  if (error) throw error;
  return data ?? null;
}

export async function uploadAarPhoto({
  aarId,
  file,
  originalFileName,
  invoker = invokeStaffMultipartFunction,
} = {}) {
  if (!aarId) throw new Error('An AAR id is required to upload a photo.');
  if (!(file instanceof File)) throw new Error('One image file is required to upload a photo.');

  const formData = new FormData();
  formData.set('aar_id', aarId);
  formData.set('file', file);

  const normalizedOriginalFileName = trimText(originalFileName || file.name);
  if (normalizedOriginalFileName) {
    formData.set('original_file_name', normalizedOriginalFileName);
  }

  const { data, error } = await invoker('aar-upload-photo', formData);

  if (error) throw error;
  if (!data?.ok) {
    throw new Error(data?.error || 'Unable to upload the photo right now.');
  }

  return data;
}

export async function uploadAarPhotoFilesSequentially({
  aarId,
  files = [],
  uploadPhoto = uploadAarPhoto,
  onFileStart,
  onFileSuccess,
  onFileError,
} = {}) {
  const queueItems = buildAarPhotoUploadQueueItems(files);
  let successCount = 0;
  const items = [];

  for (let index = 0; index < queueItems.length; index += 1) {
    const queueItem = queueItems[index];
    onFileStart?.(queueItem);

    try {
      const result = await uploadPhoto({
        aarId,
        file: queueItem.file,
        originalFileName: queueItem.file?.name,
      });

      successCount += 1;
      const nextItem = {
        ...queueItem,
        status: 'success',
        message: `Uploaded as ${result?.photo?.filename || queueItem.name}.`,
        result,
      };
      items.push(nextItem);
      onFileSuccess?.(nextItem);
    } catch (error) {
      const nextItem = {
        ...queueItem,
        status: 'error',
        message: error?.message || 'Unable to upload this photo right now.',
        error,
      };
      items.push(nextItem);
      onFileError?.(nextItem);
    }
  }

  return {
    totalCount: queueItems.length,
    successCount,
    failureCount: queueItems.length - successCount,
    items,
  };
}

export async function fetchAarPhotoPreviewBlob({
  photoId,
  invoker = invokeStaffBlobFunction,
} = {}) {
  if (!photoId) throw new Error('An AAR photo id is required to fetch a preview.');

  const blob = await invoker('aar-photo-preview', {
    photo_id: photoId,
  });

  if (!(blob instanceof Blob)) {
    throw new Error('Unable to load the AAR photo preview right now.');
  }

  return blob;
}

function getAarPhotoFilenameExtension(mimeType) {
  const extensionByMimeType = {
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
    'image/heic': '.heic',
    'image/heif': '.heif',
  };

  return extensionByMimeType[normalizeText(mimeType)] || '';
}

export function getAarPhotoDisplayFilename(photo = {}) {
  const savedFilename = trimText(photo?.filename);
  if (savedFilename) return savedFilename;

  return `AAR photo${getAarPhotoFilenameExtension(photo?.mime_type)}`;
}

export function normalizeAarPhotoDownloadFilename(filename, mimeType) {
  const fallback = `AAR photo${getAarPhotoFilenameExtension(mimeType)}`;
  const normalized = Array.from(trimText(filename || fallback)
    .replace(/[\\/]/g, '-')
    .trim())
    .filter((character) => {
      const characterCode = character.charCodeAt(0);
      return characterCode >= 32 && characterCode !== 127;
    })
    .join('')
    .trim();

  return normalized || fallback;
}

export async function downloadAarPhoto({
  photoId,
  filename,
  mimeType,
  blob = null,
  fetchPreview = fetchAarPhotoPreviewBlob,
  documentObject = typeof document !== 'undefined' ? document : null,
  urlApi = typeof URL !== 'undefined' ? URL : null,
} = {}) {
  if (!photoId && !(blob instanceof Blob)) {
    throw new Error('An AAR photo id is required to download a photo.');
  }

  const photoBlob = blob instanceof Blob
    ? blob
    : await fetchPreview({ photoId });

  if (!(photoBlob instanceof Blob) || Number(photoBlob.size) <= 0) {
    throw new Error('Unable to download this AAR photo right now.');
  }
  if (!documentObject?.body || typeof documentObject.createElement !== 'function') {
    throw new Error('Photo downloads are not available in this browser.');
  }
  if (!urlApi || typeof urlApi.createObjectURL !== 'function') {
    throw new Error('Photo downloads are not available in this browser.');
  }

  const objectUrl = urlApi.createObjectURL(photoBlob);
  const link = documentObject.createElement('a');
  link.href = objectUrl;
  link.download = normalizeAarPhotoDownloadFilename(filename, mimeType || photoBlob.type);
  link.style.display = 'none';
  documentObject.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => {
    if (typeof urlApi.revokeObjectURL === 'function') {
      urlApi.revokeObjectURL(objectUrl);
    }
  }, 0);

  return photoBlob;
}

function getAarPhotoLibraryDateTimestamp(value) {
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

export function getAarPhotoLibraryQuarter(eventDate) {
  const parsedDate = new Date(eventDate);
  if (!eventDate || !Number.isFinite(parsedDate.getTime())) {
    return {
      key: 'date-unavailable',
      label: 'Date unavailable',
      sortValue: -1,
    };
  }

  const year = parsedDate.getUTCFullYear();
  const quarter = Math.floor(parsedDate.getUTCMonth() / 3) + 1;
  return {
    key: `${year}-q${quarter}`,
    label: `Q${quarter} ${year}`,
    sortValue: (year * 10) + quarter,
  };
}

function getAarPhotoLibrarySubmitter(aar = {}, summary = {}) {
  const payload = aar?.aar_payload_json && typeof aar.aar_payload_json === 'object'
    ? aar.aar_payload_json
    : {};
  const candidates = [
    payload?.submitter?.name_raw,
    payload?.submitter?.name,
    summary?.owner_display,
    summary?.created_by_display,
  ];

  return candidates
    .map(trimText)
    .find((value) => value && normalizeText(value) !== 'unassigned') || '';
}

export function buildAarPhotoLibraryRows({
  photos = [],
  aars = [],
  summaries = [],
} = {}) {
  const aarById = new Map(
    (Array.isArray(aars) ? aars : [])
      .filter((aar) => trimText(aar?.id))
      .map((aar) => [aar.id, aar])
  );
  const summaryByAarId = new Map(
    (Array.isArray(summaries) ? summaries : [])
      .filter((summary) => trimText(summary?.aar_id))
      .map((summary) => [summary.aar_id, summary])
  );

  return (Array.isArray(photos) ? photos : [])
    .map((photo) => {
      const aar = aarById.get(photo?.aar_id);
      if (!aar) return null;

      const summary = summaryByAarId.get(photo.aar_id) || {};
      const payload = aar?.aar_payload_json && typeof aar.aar_payload_json === 'object'
        ? aar.aar_payload_json
        : {};
      const eventId = trimText(photo?.event_id || summary?.event_id || aar?.event_id);
      const eventName = trimText(
        summary?.event_name
        || aar?.event_name_snapshot
        || payload?.event?.title
      ) || 'Untitled event';
      const eventDate = trimText(
        summary?.event_date_utc
        || aar?.event_date_utc_snapshot
        || payload?.event?.date_utc
        || payload?.event?.date
      ) || null;
      const quarter = getAarPhotoLibraryQuarter(eventDate);

      return {
        ...photo,
        aar_id: aar.id,
        event_id: eventId || null,
        event_key: eventId ? `event-${eventId}` : `aar-${aar.id}`,
        event_name: eventName,
        event_date: eventDate,
        event_date_timestamp: getAarPhotoLibraryDateTimestamp(eventDate),
        filename: getAarPhotoDisplayFilename(photo),
        submitter_display: getAarPhotoLibrarySubmitter(aar, summary),
        workflow_type: trimText(aar?.workflow_type || summary?.workflow_type) || 'full',
        quarter_key: quarter.key,
        quarter_label: quarter.label,
        quarter_sort_value: quarter.sortValue,
        upload_timestamp: getAarPhotoLibraryDateTimestamp(photo?.created_at),
      };
    })
    .filter(Boolean);
}

export function sortAarPhotoLibraryRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).slice().sort((a, b) => (
    (Number(b?.quarter_sort_value) - Number(a?.quarter_sort_value))
    || (Number(b?.event_date_timestamp) - Number(a?.event_date_timestamp))
    || String(a?.event_name || '').localeCompare(String(b?.event_name || ''))
    || (Number(b?.upload_timestamp) - Number(a?.upload_timestamp))
    || String(a?.filename || '').localeCompare(String(b?.filename || ''))
  ));
}

export function filterAarPhotoLibraryRows(rows = [], filters = {}) {
  const search = normalizeText(filters?.search);

  return sortAarPhotoLibraryRows(rows).filter((row) => {
    if (filters?.quarter && row.quarter_key !== filters.quarter) return false;
    if (filters?.event && row.event_key !== filters.event) return false;
    if (filters?.submitter && normalizeText(row.submitter_display) !== normalizeText(filters.submitter)) {
      return false;
    }
    if (filters?.workflow && row.workflow_type !== filters.workflow) return false;

    if (!search) return true;
    return [row.event_name, row.submitter_display, row.filename]
      .some((value) => normalizeText(value).includes(search));
  });
}

export function groupAarPhotoLibraryRows(rows = []) {
  const quarterGroups = new Map();

  sortAarPhotoLibraryRows(rows).forEach((row) => {
    if (!quarterGroups.has(row.quarter_key)) {
      quarterGroups.set(row.quarter_key, {
        key: row.quarter_key,
        label: row.quarter_label,
        events: [],
        photo_count: 0,
        eventMap: new Map(),
      });
    }

    const quarterGroup = quarterGroups.get(row.quarter_key);
    if (!quarterGroup.eventMap.has(row.event_key)) {
      const eventGroup = {
        key: row.event_key,
        name: row.event_name,
        date: row.event_date,
        photos: [],
      };
      quarterGroup.eventMap.set(row.event_key, eventGroup);
      quarterGroup.events.push(eventGroup);
    }

    quarterGroup.eventMap.get(row.event_key).photos.push(row);
    quarterGroup.photo_count += 1;
  });

  return Array.from(quarterGroups.values()).map(({ eventMap, ...group }) => group);
}

export async function fetchProfileCapabilities({ userId, client = supabase } = {}) {
  if (!userId) return [];

  const { data, error } = await client
    .from('profile_capabilities')
    .select('capability')
    .eq('profile_id', userId);

  if (error) throw error;

  return (Array.isArray(data) ? data : [])
    .map((row) => normalizeCapability(row?.capability))
    .filter(Boolean);
}

export async function fetchProfileSummary({ profileId, client = supabase } = {}) {
  if (!profileId) return null;

  const { data, error } = await client
    .from('profiles')
    .select('id, full_name, email')
    .eq('id', profileId)
    .maybeSingle();

  if (error) throw error;
  return data ?? null;
}

function mapAarReportingRow(row = {}) {
  if (!row) return null;

  return {
    ...row,
    id: row?.aar_id || row?.id || null,
    event_name_snapshot: row?.event_name || row?.event_name_snapshot || '',
    event_date_utc_snapshot: row?.event_date_utc || row?.event_date_utc_snapshot || null,
    source: row?.event_source || row?.source || '',
    event_type: row?.event_type || '',
    photo_count: getAarStoredPhotoCount(row),
  };
}

function mapHistoricalAarReviewRow(row = {}) {
  if (!row) return null;

  return {
    ...row,
    issue_flags: normalizeStringArray(row.issue_flags),
    blocking_issue_flags: normalizeStringArray(row.blocking_issue_flags),
    informational_issue_flags: normalizeStringArray(row.informational_issue_flags),
    review_reason_categories: normalizeStringArray(row.review_reason_categories),
  };
}

function mapHistoricalAarReviewRpcRow(data) {
  if (Array.isArray(data)) {
    return mapHistoricalAarReviewRow(data[0] ?? null);
  }

  return mapHistoricalAarReviewRow(data ?? null);
}

function nullifEmpty(value) {
  const normalizedValue = trimText(value);
  return normalizedValue || null;
}

function isOwnAar(row = {}, userId) {
  if (!userId) return false;
  return row?.owner_id === userId || row?.created_by === userId;
}

export function isCurrentAar(row = {}) {
  return !isHistoricalImportedAar(row);
}

export function isSubmittedAar(row = {}) {
  return normalizeText(row?.status) === 'submitted';
}

export function isIncompleteDraftAar(row = {}) {
  return isCurrentAar(row) && ['draft', 'partial', 'incomplete'].includes(normalizeText(row?.status));
}

export function isFollowUpRequiredAar(row = {}) {
  return row?.follow_up_required === true;
}

export function isMissingPhotosAar(row = {}) {
  return isCurrentAar(row) && getAarStoredPhotoCount(row) === 0;
}

export function isAttentionRequiredAar(row = {}) {
  if (!isCurrentAar(row)) return false;
  return isSubmittedAar(row) || isIncompleteDraftAar(row) || isFollowUpRequiredAar(row);
}

// Operational attention is limited to concrete work items. The submitted /
// awaiting-review state is excluded because routine AARs are not a review
// backlog, and photo absence is excluded because many legitimate AARs (virtual
// presentations especially) never have photos.
export function isOperationalAttentionAar(row = {}) {
  if (!isCurrentAar(row)) return false;
  return isFollowUpRequiredAar(row) || isIncompleteDraftAar(row);
}

function getAarSortTimestamp(row = {}) {
  const timestamps = [row?.submitted_at, row?.updated_at, row?.created_at]
    .map((value) => new Date(value).getTime())
    .filter((value) => Number.isFinite(value));

  return timestamps.length > 0 ? Math.max(...timestamps) : 0;
}

export function buildAarHealthMetrics({
  userId,
  myRows = [],
  teamRows = [],
  historicalNeedsReviewCount = null,
  historicalImportStatusCounts = null,
  includeTeamMetrics = false,
} = {}) {
  const normalizedMyRows = (Array.isArray(myRows) ? myRows : []).filter((row) => isOwnAar(row, userId));
  const normalizedTeamRows = Array.isArray(teamRows) ? teamRows : [];

  const metrics = {
    my_current_aars: normalizedMyRows.filter((row) => isCurrentAar(row)).length,
    my_submitted_not_reviewed: normalizedMyRows.filter((row) => isSubmittedAar(row)).length,
    my_incomplete_drafts: normalizedMyRows.filter((row) => isIncompleteDraftAar(row)).length,
    my_follow_up_required: normalizedMyRows.filter((row) => isCurrentAar(row) && isFollowUpRequiredAar(row)).length,
    my_no_photos_attached: normalizedMyRows.filter((row) => isMissingPhotosAar(row)).length,
  };

  if (!includeTeamMetrics) {
    return metrics;
  }

  return {
    ...metrics,
    team_current_aars: normalizedTeamRows.filter((row) => isCurrentAar(row)).length,
    team_submitted_not_reviewed: normalizedTeamRows.filter((row) => isCurrentAar(row) && isSubmittedAar(row)).length,
    team_incomplete_drafts: normalizedTeamRows.filter((row) => isIncompleteDraftAar(row)).length,
    team_follow_up_required: normalizedTeamRows.filter((row) => isCurrentAar(row) && isFollowUpRequiredAar(row)).length,
    team_no_photos_attached: normalizedTeamRows.filter((row) => isMissingPhotosAar(row)).length,
    team_missing_photos: normalizedTeamRows.filter((row) => isMissingPhotosAar(row)).length,
    historical_imports: normalizedTeamRows.filter((row) => isHistoricalImportedAar(row)).length,
    historical_needs_review: Number.isFinite(Number(historicalNeedsReviewCount))
      ? Number(historicalNeedsReviewCount)
      : null,
    historical_status_counts: historicalImportStatusCounts && typeof historicalImportStatusCounts === 'object'
      ? {
        needs_review: Number(historicalImportStatusCounts.needs_review || 0),
        approved: Number(historicalImportStatusCounts.approved || 0),
        skipped: Number(historicalImportStatusCounts.skipped || 0),
        promoted: Number(historicalImportStatusCounts.promoted || 0),
        error: Number(historicalImportStatusCounts.error || 0),
      }
      : null,
  };
}

export function buildRecentAarAttentionItems(rows = [], { limit = 5 } = {}) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => isAttentionRequiredAar(row))
    .slice()
    .sort((a, b) => getAarSortTimestamp(b) - getAarSortTimestamp(a))
    .slice(0, Math.max(Number(limit) || 0, 0));
}

export function buildOperationalAarAttentionItems(rows = [], { limit = 5 } = {}) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => isOperationalAttentionAar(row))
    .slice()
    .sort((a, b) => getAarSortTimestamp(b) - getAarSortTimestamp(a))
    .slice(0, Math.max(Number(limit) || 0, 0));
}

export async function fetchAars({ scope = 'mine', userId, client = supabase } = {}) {
  let query = client
    .from('v_aar_reporting_summary')
    .select(AAR_REPORTING_SELECT_FIELDS);

  if (scope === 'mine') {
    if (!userId) return [];
    query = query.or(`created_by.eq.${userId},owner_id.eq.${userId}`);
  }

  query = query.in('event_type', AAR_REPORTING_EVENT_TYPES);

  const { data, error } = await query
    .order('submitted_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false, nullsFirst: false });

  if (error) throw error;
  return (Array.isArray(data) ? data : []).map(mapAarReportingRow).filter(Boolean);
}

export function normalizeAarPhotoLibraryEdgeRows(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const id = trimText(item?.id);
      const aarId = trimText(item?.aar_id);
      if (!id || !aarId) return null;

      const eventId = trimText(item?.event_id);
      const eventDate = trimText(item?.event_date) || null;
      const quarter = getAarPhotoLibraryQuarter(eventDate);

      return {
        id,
        aar_id: aarId,
        event_id: eventId || null,
        event_key: eventId ? `event-${eventId}` : `aar-${aarId}`,
        filename: getAarPhotoDisplayFilename(item),
        mime_type: trimText(item?.mime_type) || null,
        file_size_bytes: item?.file_size_bytes !== null
          && item?.file_size_bytes !== undefined
          && item?.file_size_bytes !== ''
          && Number.isFinite(Number(item.file_size_bytes))
          ? Number(item.file_size_bytes)
          : null,
        sort_order: item?.sort_order !== null
          && item?.sort_order !== undefined
          && item?.sort_order !== ''
          && Number.isFinite(Number(item.sort_order))
          ? Number(item.sort_order)
          : null,
        created_at: trimText(item?.created_at) || null,
        event_name: trimText(item?.event_name) || 'Untitled event',
        event_date: eventDate,
        event_date_timestamp: getAarPhotoLibraryDateTimestamp(eventDate),
        submitter_display: trimText(item?.submitter_display),
        workflow_type: trimText(item?.workflow_type) || 'full',
        can_view_aar: item?.can_view_aar === true,
        quarter_key: quarter.key,
        quarter_label: quarter.label,
        quarter_sort_value: quarter.sortValue,
        upload_timestamp: getAarPhotoLibraryDateTimestamp(item?.created_at),
      };
    })
    .filter(Boolean);
}

export async function fetchAarPhotoLibraryRows({
  invoker = invokeStaffFunction,
} = {}) {
  const { data, error } = await invoker('aar-photo-library', {});

  if (error) throw error;
  if (!data?.ok) {
    throw new Error(data?.error || 'Unable to load the AAR Photo Library right now.');
  }

  return sortAarPhotoLibraryRows(normalizeAarPhotoLibraryEdgeRows(data.photos));
}

export async function fetchAarReportingSummaryById({ aarId, client = supabase } = {}) {
  if (!aarId) return null;

  const { data, error } = await client
    .from('v_aar_reporting_summary')
    .select(AAR_REPORTING_SELECT_FIELDS)
    .eq('aar_id', aarId)
    .maybeSingle();

  if (error) throw error;
  return mapAarReportingRow(data ?? null);
}

export async function fetchAarById({ aarId, client = supabase } = {}) {
  if (!aarId) return null;

  const { data, error } = await client
    .from('after_action_reports')
    .select(AAR_DETAIL_SELECT_FIELDS)
    .eq('id', aarId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const linkedEvent = data.event_id
    ? await fetchAarEvent({ eventId: data.event_id, client })
    : null;

  return {
    ...data,
    linked_event: linkedEvent,
  };
}

export async function fetchAarEvent({ eventId, client = supabase } = {}) {
  if (!eventId) return null;

  const { data, error } = await client
    .from('events')
    .select(AAR_EVENT_SELECT_FIELDS)
    .eq('id', eventId)
    .maybeSingle();

  if (error) throw error;
  return data ?? null;
}

export async function fetchAarPhotos({ aarId, client = supabase } = {}) {
  if (!aarId) return [];

  const { data, error } = await client
    .from('after_action_report_photos')
    .select(AAR_PHOTO_SELECT_FIELDS)
    .eq('aar_id', aarId)
    .order('sort_order', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: true, nullsFirst: false });

  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function fetchAarImportNeedsReviewCount({ client = supabase } = {}) {
  const { count, error } = await client
    .from('v_aar_import_review_queue_v1')
    .select('id', { count: 'exact', head: true })
    .eq('import_status', 'needs_review');

  if (error) throw error;
  return Number(count || 0);
}

export async function fetchAarImportStatusCounts({ client = supabase } = {}) {
  const { data, error } = await client
    .from('v_aar_import_review_queue_v1')
    .select('import_status');

  if (error) throw error;

  return (Array.isArray(data) ? data : []).reduce((accumulator, row) => {
    const key = normalizeText(row?.import_status);
    if (Object.prototype.hasOwnProperty.call(accumulator, key)) {
      accumulator[key] += 1;
    }
    return accumulator;
  }, {
    needs_review: 0,
    approved: 0,
    skipped: 0,
    promoted: 0,
    error: 0,
  });
}

export async function fetchHistoricalAarReviewRows({ client = supabase } = {}) {
  const { data, error } = await client
    .from('v_aar_import_review_queue_v1')
    .select(AAR_IMPORT_REVIEW_SELECT_FIELDS)
    .order('source_row_number', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false, nullsFirst: false });

  if (error) throw error;
  return (Array.isArray(data) ? data : []).map(mapHistoricalAarReviewRow).filter(Boolean);
}

export async function fetchHistoricalAarReviewRowById({ rowId, client = supabase } = {}) {
  if (!rowId) return null;

  const { data, error } = await client
    .from('v_aar_import_review_queue_v1')
    .select(AAR_IMPORT_REVIEW_SELECT_FIELDS)
    .eq('id', rowId)
    .maybeSingle();

  if (error) throw error;
  return mapHistoricalAarReviewRow(data ?? null);
}

export async function fetchHistoricalAarReviewDuplicatePeers({
  duplicateGroupKey,
  excludeRowId,
  client = supabase,
} = {}) {
  if (!trimText(duplicateGroupKey)) return [];

  const { data, error } = await client
    .from('v_aar_import_review_queue_v1')
    .select(AAR_IMPORT_REVIEW_SELECT_FIELDS)
    .eq('duplicate_group_key', duplicateGroupKey)
    .order('source_row_number', { ascending: true, nullsFirst: false });

  if (error) throw error;

  return (Array.isArray(data) ? data : [])
    .map(mapHistoricalAarReviewRow)
    .filter((row) => row && row.id !== excludeRowId);
}

export async function saveHistoricalAarImportReview({
  rowId,
  correctedEventTitle,
  correctedEventDate,
  correctedSubmitterName,
  correctedWorkflowType,
  correctedState,
  duplicateResolution,
  reviewerNotes,
  client = supabase,
} = {}) {
  if (!rowId) throw new Error('A historical review row id is required.');

  const { data, error } = await client.rpc('save_aar_import_review_v1', {
    p_row_id: rowId,
    p_corrected_event_title: nullifEmpty(correctedEventTitle),
    p_corrected_event_date: nullifEmpty(correctedEventDate),
    p_corrected_submitter_name: nullifEmpty(correctedSubmitterName),
    p_corrected_workflow_type: nullifEmpty(correctedWorkflowType),
    p_corrected_state: nullifEmpty(correctedState),
    p_duplicate_resolution: nullifEmpty(duplicateResolution),
    p_reviewer_notes: nullifEmpty(reviewerNotes),
  });

  if (error) throw error;
  return mapHistoricalAarReviewRpcRow(data);
}

export async function approveHistoricalAarImportReview({
  rowId,
  reviewerNotes,
  client = supabase,
} = {}) {
  if (!rowId) throw new Error('A historical review row id is required.');

  const { data, error } = await client.rpc('approve_aar_import_review_v1', {
    p_row_id: rowId,
    p_reviewer_notes: nullifEmpty(reviewerNotes),
  });

  if (error) throw error;
  return mapHistoricalAarReviewRpcRow(data);
}

export async function skipHistoricalAarImportReview({
  rowId,
  reviewerNotes,
  duplicateResolution,
  client = supabase,
} = {}) {
  if (!rowId) throw new Error('A historical review row id is required.');

  const { data, error } = await client.rpc('skip_aar_import_review_v1', {
    p_row_id: rowId,
    p_reviewer_notes: nullifEmpty(reviewerNotes),
    p_duplicate_resolution: nullifEmpty(duplicateResolution),
  });

  if (error) throw error;
  return mapHistoricalAarReviewRpcRow(data);
}

export async function fetchAarHealthMetrics({
  userId,
  capabilities = [],
  client = supabase,
  attentionLimit = 5,
} = {}) {
  const teamAccessEnabled = hasAarTeamAccess(capabilities);

  if (!userId) {
    return {
      metrics: buildAarHealthMetrics({ userId }),
      attentionItems: [],
      operationalAttentionItems: [],
      warnings: {},
    };
  }

  const rowsPromise = fetchAars({
    scope: teamAccessEnabled ? 'team' : 'mine',
    userId,
    client,
  });

  const historicalNeedsReviewPromise = teamAccessEnabled
    ? fetchAarImportNeedsReviewCount({ client })
      .then((count) => ({ count, error: null }))
      .catch((error) => ({ count: null, error }))
    : Promise.resolve({ count: null, error: null });
  const historicalStatusCountsPromise = teamAccessEnabled
    ? fetchAarImportStatusCounts({ client })
      .then((counts) => ({ counts, error: null }))
      .catch((error) => ({ counts: null, error }))
    : Promise.resolve({ counts: null, error: null });

  const [rows, historicalNeedsReviewResult, historicalStatusCountsResult] = await Promise.all([
    rowsPromise,
    historicalNeedsReviewPromise,
    historicalStatusCountsPromise,
  ]);

  const teamRows = teamAccessEnabled ? rows : [];
  const myRows = teamAccessEnabled
    ? teamRows.filter((row) => isOwnAar(row, userId))
    : rows;

  return {
    metrics: buildAarHealthMetrics({
      userId,
      myRows,
      teamRows,
      historicalNeedsReviewCount: historicalNeedsReviewResult.count,
      historicalImportStatusCounts: historicalStatusCountsResult.counts,
      includeTeamMetrics: teamAccessEnabled,
    }),
    attentionItems: teamAccessEnabled
      ? buildRecentAarAttentionItems(teamRows, { limit: attentionLimit })
      : [],
    operationalAttentionItems: buildOperationalAarAttentionItems(
      teamAccessEnabled ? teamRows : myRows,
      { limit: attentionLimit }
    ),
    warnings: {
      historicalNeedsReview: historicalNeedsReviewResult.error,
      historicalStatusCounts: historicalStatusCountsResult.error,
    },
  };
}
